import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["title", "description", "error", "warning", "success", "info", "okButtonLabel", "closeButtonLabel", "htmlContents", "children", "buttons"];
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import { makeStyles } from "@material-ui/core/styles";
import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import SuccessIcon from "@material-ui/icons/Check";
import ErrorIcon from "@material-ui/icons/ErrorOutline";
import WarnIcon from "@material-ui/icons/ReportProblemOutlined";
import Typography from "@material-ui/core/Typography";
import CloseableDialog from "./CloseableDialog";
import { HtmlString } from "../../Utils";
var useStyles = makeStyles(function (theme) {
  return {
    successIcon: {
      color: "limegreen",
      marginRight: "10px"
    },
    errorIcon: {
      color: "red",
      marginRight: "10px"
    },
    warningIcon: {
      color: "orange",
      marginRight: "10px"
    },
    infoIcon: {
      color: "gray",
      marginRight: "10px"
    }
  };
});

/**
 * Hook which returns props required by the MessageDialog as well as a function to set a message.
 */
export function useMessageDialog() {
  var _useState = useState({
      open: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    dialogProps = _useState2[0],
    setDialogProps = _useState2[1];
  var setMessage = function setMessage(newMessage) {
    if (newMessage) {
      setDialogProps({
        open: true,
        description: newMessage
      });
    } else {
      setDialogProps(_objectSpread(_objectSpread({}, dialogProps), {
        open: false
      }));
    }
  };
  var clearMessage = function clearMessage() {
    setMessage(null);
  };
  var dialogPropsWithHandleClose = _objectSpread(_objectSpread({}, dialogProps), {
    handleClose: clearMessage
  });
  return [dialogPropsWithHandleClose, setMessage];
}
export default function MessageDialog(_ref) {
  var title = _ref.title,
    description = _ref.description,
    error = _ref.error,
    warning = _ref.warning,
    success = _ref.success,
    info = _ref.info,
    okButtonLabel = _ref.okButtonLabel,
    closeButtonLabel = _ref.closeButtonLabel,
    htmlContents = _ref.htmlContents,
    children = _ref.children,
    buttons = _ref.buttons,
    props = _objectWithoutProperties(_ref, _excluded);
  var classes = useStyles();
  var icon;
  if (error) {
    icon = /*#__PURE__*/React.createElement(ErrorIcon, {
      className: classes.errorIcon
    });
    if (!title) title = "Error";
  } else if (warning) {
    icon = /*#__PURE__*/React.createElement(WarnIcon, {
      className: classes.warningIcon
    });
    if (!title) title = "Warning";
  } else if (success) {
    icon = /*#__PURE__*/React.createElement(SuccessIcon, {
      className: classes.successIcon
    });
    if (!title) title = "Success";
  } else if (info) {
    icon = /*#__PURE__*/React.createElement(InfoIcon, {
      className: classes.infoIcon
    });
    if (!title) title = "Info";
  }
  var contents;
  if (htmlContents) {
    contents = /*#__PURE__*/React.createElement(HtmlString, {
      text: description
    });
  } else if (Array.isArray(description)) {
    contents = /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "textSecondary"
    }, /*#__PURE__*/React.createElement("ul", null, description.map(function (message, index) {
      return /*#__PURE__*/React.createElement("li", {
        key: index
      }, message);
    })));
  } else {
    contents = description;
  }
  return /*#__PURE__*/React.createElement(CloseableDialog, Object.assign({
    maxWidth: "sm",
    fullWidth: true
  }, props, {
    title: /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center"
      }
    }, icon, title)
  }), /*#__PURE__*/React.createElement(DialogContent, {
    dividers: true
  }, /*#__PURE__*/React.createElement(DialogContentText, {
    id: "description"
  }, contents), children), /*#__PURE__*/React.createElement(DialogActions, null, buttons, props.handleOk && /*#__PURE__*/React.createElement(Button, {
    onClick: props.handleOk,
    color: "primary"
  }, okButtonLabel || "OK"), props.handleClose && /*#__PURE__*/React.createElement(Button, {
    onClick: props.handleClose,
    color: "primary",
    autoFocus: true
  }, closeButtonLabel || "Close")));
}